import type { ActorRule } from '@experiences/interfaces';
import { ActorType } from '@experiences/interfaces';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { DirectoryEntityType } from '../../../../../common/interfaces/cis/directory';
import { resolveById } from '../../../../../services/identity/DirectoryService';
import { accountGlobalId } from '../../../../../store/selectors';

/**
 * Resolves actor identity IDs and group membership IDs to display names
 * using the Directory Service (Users/Robots/Groups).
 *
 * This hook is needed because `valueNames` is a UI-only field that is stripped before API calls,
 * so when displaying policies loaded from the API, IDs need to be resolved to names.
 */
export const useActorRuleNameResolution = (actorRule: ActorRule | undefined) => {
    const organizationId = useSelector(accountGlobalId);

    // Collect non-wildcard User/Robot IDs for resolution
    const userRobotIds = useMemo(() => {
        const uIds: string[] = [];

        if (!actorRule?.values) {
            return uIds;
        }

        actorRule.values.forEach(v => {
            const isWildcard = v.values.length === 1 && v.values[0] === '*';
            if (isWildcard) {
                return;
            }

            if (v.type === ActorType.User || v.type === ActorType.Robot) {
                uIds.push(...v.values);
            }
        });

        return uIds;
    }, [ actorRule?.values ]);

    // Collect Group IDs from actorRule.values entries where type === ActorType.Group
    const groupIds = useMemo(() => {
        if (!actorRule?.values) {
            return [];
        }
        return actorRule.values
            .filter(v => v.type === ActorType.Group)
            .flatMap(v => {
                const isWildcard = v.values.length === 1 && v.values[0] === '*';
                return isWildcard ? [] : v.values;
            });
    }, [ actorRule?.values ]);

    // Resolve Users and Robots via Directory Service
    const { data: userRobotNameMap } = useSWR(
        organizationId && userRobotIds.length > 0
            ? {
                type: 'resolveActorUserRobot',
                accountId: organizationId,
                ids: userRobotIds.join(','),
            }
            : null,
        async () => {
            const results = await Promise.allSettled(
                userRobotIds.map(id =>
                    resolveById({
                        partitionGlobalId: organizationId,
                        entityId: id,
                        entityType: DirectoryEntityType.user,
                    }),
                ),
            );
            const map: Record<string, string> = {};
            results.forEach((result, idx) => {
                if (result.status === 'fulfilled') {
                    map[userRobotIds[idx]] = result.value.displayName || result.value.identityName || userRobotIds[idx];
                }
            });
            return map;
        },
    );

    // Resolve Groups via Directory Service
    const { data: groupNameMap } = useSWR(
        organizationId && groupIds.length > 0
            ? {
                type: 'resolveActorGroups',
                accountId: organizationId,
                ids: groupIds.join(','),
            }
            : null,
        async () => {
            const results = await Promise.allSettled(
                groupIds.map(id =>
                    resolveById({
                        partitionGlobalId: organizationId,
                        entityId: id,
                        entityType: DirectoryEntityType.group,
                    }),
                ),
            );
            const map: Record<string, string> = {};
            results.forEach((result, idx) => {
                if (result.status === 'fulfilled') {
                    map[groupIds[idx]] = result.value.displayName || result.value.identityName || groupIds[idx];
                }
            });
            return map;
        },
    );

    const actorIdentityNameMap = userRobotNameMap ?? {} as Record<string, string>;

    return {
        actorIdentityNameMap,
        groupNameMap: groupNameMap ?? {} as Record<string, string>,
    };
};
